import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import BadgeIcon from '@mui/icons-material/Badge';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyToClipboard } from '../../../../utils/clipboard';
import { useToast } from '../../../../contexts/ToastContext';

interface NestedProgram {
  id: number;
  name: string;
  duration_hours?: number | null;
}

interface NestedStudentProgram {
  student_id: number;
  program_id: number;
  status: string;
  start_date: string;
  end_date?: string | null;
  exam_date?: string | null;
  program: NestedProgram;
}

interface NestedStudent {
  id: number;
  full_name: string;
  email: string | null;
  login: string | null;
  password: string | null;
  position: string | null;
  student_programs: NestedStudentProgram[];
}

interface OrganizationInfo {
  name: string;
}

interface SpecialistInfo {
  name: string;
}

interface ApplicationContext {
  id: number;
  status: string;
  organization: OrganizationInfo;
  specialist: SpecialistInfo;
}

interface StudentFullInfoModalProps {
  student: NestedStudent;
  applicationContext: ApplicationContext;
  onBack: () => void;
}

const formatDate = (isoString?: string | null) => {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleDateString('ru-RU');
  } catch {
    return isoString || '';
  }
};

const getStatusBadge = (status: string) => {
  const s = status?.toLowerCase() || '';
  let bgClass = 'bg-gray-100 text-gray-800';
  let label = status;

  if (s === 'new') {
    bgClass = 'bg-blue-100 text-blue-800';
    label = 'Новая';
  } else if (s === 'sent') {
    bgClass = 'bg-green-100 text-green-800';
    label = 'Отправлено';
  }

  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide ${bgClass}`}>
      {label}
    </span>
  );
};

const getProgramStatusLabel = (status: string) => {
    const map: Record<string, string> = {
        'active': 'Активен',
        'completed': 'Завершен',
        'enrolled': 'Зачислен',
        'expelled': 'Отчислен',
        'pending': 'Ожидает',
    };
    return map[status?.toLowerCase()] || status;
};

export const StudentFullInfoModal: React.FC<StudentFullInfoModalProps> = ({
  student,
  applicationContext,
  onBack,
}) => {
  const [expandedProgramId, setExpandedProgramId] = useState<number | null>(null);

  const { addToast } = useToast();

  const toggleProgram = (progId: number) => {
    setExpandedProgramId(prev => (prev === progId ? null : progId));
  };

  const handleCopyCredentials = async () => {
      if (!student.login || !student.password) return;
      const textToCopy = `Логин: ${student.login}\nПароль: ${student.password}`;

      const success = await copyToClipboard(textToCopy);

      if (success) {
          addToast({
              message: 'Логин и пароль успешно скопированы!',
              type: 'success'
          });
      } else {
          addToast({
              message: 'Не удалось скопировать данные',
              type: 'error'
          });
      }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[95%] h-[95%] md:w-[80%] md:h-[90%] flex flex-col overflow-hidden border border-gray-200">

        {/* Header */}
        <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <button
            onClick={onBack}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 group"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Назад к списку студентов
          </button>

          <button
              onClick={onBack}
              className="p-2 rounded-full hover:bg-gray-100 transition text-gray-500 focus:outline-none"
          >
              <CloseIcon />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Card 1: Основная информация */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <BadgeIcon fontSize="small" className="text-gray-500" />
                <h3 className="font-semibold text-gray-700">Основная информация</h3>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase font-semibold mb-1">ФИО</p>
                  <p className="text-gray-900 font-bold text-lg leading-tight">{student.full_name}</p>
                </div>
                <div>
                   <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Email</p>
                   <p className="text-gray-800">{student.email || '—'}</p>
                </div>

                {/* Логин и Кнопка копирования */}
                <div>
                   <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Логин</p>
                   <div className="flex items-center gap-2">
                       <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-blue-700 select-all font-medium">
                           {student.login || '—'}
                       </span>
                       {student.login && student.password && (
                           <button
                               onClick={handleCopyCredentials}
                               className="ml-1 text-gray-400 hover:text-blue-600 transition flex items-center gap-1 bg-white hover:bg-blue-50 px-2 py-1 rounded border border-gray-200 hover:border-blue-100"
                               title="Скопировать логин и пароль"
                           >
                               <ContentCopyIcon style={{ fontSize: 16 }} />
                           </button>
                       )}
                   </div>
                </div>

                {/* Пароль */}
                <div>
                   <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Пароль</p>
                   <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-700 select-all font-medium">
                       {student.password || '—'}
                   </span>
                </div>

                 {student.position && (
                    <div>
                        <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Должность</p>
                        <p className="text-gray-800">{student.position}</p>
                    </div>
                 )}
              </div>
            </div>

            {/* Card 2: Информация о заявке */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <AssignmentIcon fontSize="small" className="text-gray-500" />
                <h3 className="font-semibold text-gray-700">Информация о заявке</h3>
              </div>
              <div className="p-5 space-y-4">
                <div>
                   <p className="text-xs text-gray-400 uppercase font-semibold mb-1">ID заявки</p>
                   <p className="text-gray-800 font-medium">{applicationContext.id}</p>
                </div>
                <div>
                   <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Статус</p>
                   <div>{getStatusBadge(applicationContext.status)}</div>
                </div>
                <div>
                   <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Организация</p>
                   <p className="text-gray-800 leading-snug">{applicationContext.organization.name}</p>
                </div>
                <div>
                   <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Ответственный специалист</p>
                   <p className="text-gray-800">{applicationContext.specialist.name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Образовательные программы */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
             <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
                <SchoolIcon fontSize="small" className="text-gray-500" />
                <h3 className="font-semibold text-gray-700">Образовательные программы</h3>
              </div>

              <div className="divide-y divide-gray-100">
                {student.student_programs.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 italic">Программы не назначены</div>
                ) : (
                    student.student_programs.map((sp) => {
                        const isExpanded = expandedProgramId === sp.program_id;
                        return (
                            <div key={sp.program_id} className="group">
                                <button
                                    onClick={() => toggleProgram(sp.program_id)}
                                    className="w-full text-left px-6 py-4 flex items-center justify-between gap-3"
                                >
                                    <div className="flex-1">
                                        <span className="font-medium text-gray-800 leading-snug block">
                                            {sp.program.name}
                                        </span>
                                        {!isExpanded && (
                                            <span className="text-xs text-gray-500 mt-1 block">
                                                Нажмите для деталей...
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-gray-400">
                                        {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                    </div>
                                </button>

                                {isExpanded && (
                                    <div className="px-6 pb-5 pt-4 border-t border-gray-100 bg-gray-50/50">
                                        <div className="space-y-2.5 max-w-md">

                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-500 w-40 shrink-0">Дата начала:</span>
                                                <span className="font-medium text-gray-800">{formatDate(sp.start_date) || '—'}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-500 w-40 shrink-0">Дата окончания:</span>
                                                <span className="font-medium text-gray-800">{formatDate(sp.end_date) || '—'}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-500 w-40 shrink-0">Дата экзамена:</span>
                                                <span className="font-medium text-gray-800">{formatDate(sp.exam_date) || '—'}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-500 w-40 shrink-0">Часы:</span>
                                                <span className="font-medium text-gray-800">{sp.program.duration_hours?.toString() || '—'}</span>
                                            </div>

                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-gray-500 w-40 shrink-0">Статус обучения:</span>
                                                <span className="font-medium text-gray-800">{getProgramStatusLabel(sp.status)}</span>
                                            </div>

                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
              </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
                onClick={onBack}
                className="px-6 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium shadow-sm transition"
            >
                Закрыть
            </button>
        </div>

      </div>
    </div>
  );
};